/**
 * IDF (inverse document frequency) weighting for the Feature<->flag matcher.
 *
 * Down-weights generic tokens (e.g. "feature", present in dozens of entries)
 * automatically, without a hand-curated stopword list that would need
 * updating as the doc corpus grows.
 *
 * @see docs/superpowers/specs/2026-08-09-atlas-flag-cluster-linking-design.md §1
 */

/**
 * Standard idf: log(N / df). A token in every document scores 0 (no
 * discriminating power); a token in exactly 1 of N documents scores log(N)
 * (its full weight). Each `tokenSets` entry is one "document" — a flagId's
 * tokens, a description's tokens, a Feature label's tokens, etc. — deduped
 * per-document before counting so repeated words within one document don't
 * inflate df.
 */
export function computeIdf(tokenSets: string[][]): Map<string, number> {
  const n = tokenSets.length;
  const df = new Map<string, number>();
  for (const tokens of tokenSets) {
    for (const token of new Set(tokens)) {
      df.set(token, (df.get(token) ?? 0) + 1);
    }
  }
  const idf = new Map<string, number>();
  for (const [token, count] of df) {
    idf.set(token, Math.log(n / count));
  }
  return idf;
}

/**
 * IDF-weighted Jaccard overlap between two token lists, in [0, 1]:
 * sum(idf(t) for t in intersection) / sum(idf(t) for t in union).
 * Unknown tokens (idf undefined — not in the corpus) contribute 0 weight
 * rather than throwing, so the caller can score against fabricated/test
 * tokens without pre-registering them.
 */
export function idfWeightedOverlap(a: readonly string[], b: readonly string[], idf: Map<string, number>): number {
  const setA = new Set(a);
  const setB = new Set(b);
  const union = new Set([...setA, ...setB]);
  if (union.size === 0) return 0;

  let intersectionWeight = 0;
  let unionWeight = 0;
  for (const token of union) {
    const weight = idf.get(token) ?? 0;
    unionWeight += weight;
    if (setA.has(token) && setB.has(token)) intersectionWeight += weight;
  }
  if (unionWeight === 0) return 0;
  return intersectionWeight / unionWeight;
}
